import React, { useState, useEffect, useRef } from "react";
import axios from "../axios";

const MAGASINS_GUYANE = [
  "Carrefour Cayenne", "Carrefour Market Matoury",
  "Match Cayenne", "Leader Price Cayenne",
  "Leader Price Matoury", "Leader Price Kourou",
  "Leader Price Saint-Laurent", "Hyper U Kourou",
  "Super U Saint-Laurent", "Kiabi Cayenne",
  "Gémo Cayenne", "Gémo Kourou", "La Halle Cayenne",
  "Jennyfer Cayenne", "Pimkie Cayenne",
  "Mr Bricolage Cayenne", "Mr Bricolage Kourou",
  "Gifi Cayenne", "Gifi Kourou",
];

/**
 * StoreAutocomplete — champ de recherche magasin
 * Props :
 *   value        — valeur actuelle (string)
 *   onChange(v)  — callback quand une valeur est sélectionnée ou saisie
 *   lat, lng     — coordonnées GPS optionnelles pour prioriser les résultats proches
 *   disabled     — boolean
 *   inputStyle   — style objet à appliquer au champ
 */
const StoreAutocomplete = ({ value, onChange, lat, lng, disabled, inputStyle }) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Fermer les suggestions si clic en dehors
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 2) {
      // Moins de 2 caractères → fallback liste locale Guyane filtrée
      const local = MAGASINS_GUYANE.filter(m =>
        m.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 5);
      setSuggestions(local.map(l => ({ label: l, name: l })));
      setShowSuggestions(val.length > 0);
      return;
    }

    // Debounce 400ms avant appel API
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { query: val };
        if (lat && lng) { params.lat = lat; params.lng = lng; }
        const res = await axios.get("/scans/places/search", { params });
        const apiResults = res.data?.results || [];

        // Fusionner résultats API + liste locale, dédoublonner par label
        const local = MAGASINS_GUYANE
          .filter(m => m.toLowerCase().includes(val.toLowerCase()))
          .map(l => ({ label: l, name: l }));
        const merged = [...local, ...apiResults].reduce((acc, item) => {
          if (!acc.find(a => a.label === item.label)) acc.push(item);
          return acc;
        }, []).slice(0, 7);

        setSuggestions(merged);
        setShowSuggestions(merged.length > 0);
      } catch (err) {
        console.warn("[StoreAutocomplete] API indisponible, fallback liste locale :", err?.message);
        // Fallback sur la liste locale
        const local = MAGASINS_GUYANE
          .filter(m => m.toLowerCase().includes(val.toLowerCase()))
          .slice(0, 5)
          .map(l => ({ label: l, name: l }));
        setSuggestions(local);
        setShowSuggestions(local.length > 0);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleSelect = (item) => {
    setQuery(item.label);
    onChange({ label: item.label, name: item.name, lat: item.lat ?? null, lng: item.lng ?? null });
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", marginBottom: "10px" }}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => query.length > 0 && setShowSuggestions(suggestions.length > 0)}
        placeholder="Magasin / lieu (optionnel)"
        disabled={disabled}
        style={inputStyle}
        autoComplete="off"
      />
      {loading && (
        <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#94a3b8" }}>
          🔍
        </span>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <ul style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "white", border: "1.5px solid #CBD5E1",
          borderRadius: "8px", marginTop: "2px", padding: 0,
          listStyle: "none", zIndex: 100, maxHeight: "200px",
          overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
        }}>
          {suggestions.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => handleSelect(s)}
              style={{
                padding: "10px 12px", fontSize: "13px", cursor: "pointer",
                borderBottom: i < suggestions.length - 1 ? "1px solid #F1F5F9" : "none",
                color: "#1e293b"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}
            >
              🏪 {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StoreAutocomplete;
